import type { RefObject } from 'react';
import type { ThemeContextValue } from '@course-authoring/shared-types';
import type { ClickExpandTopic } from '@course-authoring/shared-types';
import type { JSONContent } from '@tiptap/core';

import { ClickExpandRichFieldEditor } from './ClickExpandRichFieldEditor';
import type { ClickExpandEditorStyles } from './clickExpandEditorStyles';
import type { ClickExpandFieldName } from './clickExpandEditorTypes';

interface ClickExpandTopicEditorProps {
  topic: ClickExpandTopic;
  index: number;
  canRemoveTopic: boolean;
  focusedField: ClickExpandFieldName | null;
  onFieldFocus: (fieldName: ClickExpandFieldName) => void;
  onFieldBlur: () => void;
  onTitleChange: (topicId: string, title: string) => void;
  onContentChange: (topicId: string, content: JSONContent) => void;
  onImageInsert: (topicId: string) => void;
  onImageRemove: (topicId: string) => void;
  onRemove: (topicId: string) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  styles: ClickExpandEditorStyles;
  tokens: ThemeContextValue['tokens'];
  translationMode?: boolean;
}

export function ClickExpandTopicEditor({
  topic,
  index,
  canRemoveTopic,
  focusedField,
  onFieldFocus,
  onFieldBlur,
  onTitleChange,
  onContentChange,
  onImageInsert,
  onImageRemove,
  onRemove,
  containerRef,
  styles,
  tokens,
  translationMode = false,
}: ClickExpandTopicEditorProps) {
  const contentFieldName = `topic.${topic.id}.content` as ClickExpandFieldName;

  return (
    <div style={styles.topicContainer}>
      <div style={styles.topicHeader}>
        <span style={styles.topicLabel}>Topic {index + 1}</span>
        {!translationMode && canRemoveTopic && (
          <button
            type="button"
            style={styles.deleteButton}
            onClick={() => onRemove(topic.id)}
            title="Remove topic"
            onMouseEnter={(event) => {
              event.currentTarget.style.color = '#b91c1c';
              event.currentTarget.style.backgroundColor = tokens.backgrounds.surfaceAlt;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.color = tokens.text.muted;
              event.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            &times;
          </button>
        )}
      </div>

      <div style={styles.fieldWrapper}>
        <div style={styles.fieldLabel}>Title</div>
        <input
          type="text"
          value={topic.title}
          placeholder="Topic title..."
          onChange={(event) => onTitleChange(topic.id, event.target.value)}
          style={styles.topicTitleInput}
        />
      </div>

      <div style={styles.fieldWrapper}>
        <div style={styles.fieldLabel}>Content</div>
        <ClickExpandRichFieldEditor
          content={topic.content}
          placeholder="Enter topic content..."
          isFocused={focusedField === contentFieldName}
          onFocus={() => onFieldFocus(contentFieldName)}
          onBlur={onFieldBlur}
          onContentChange={(content) => onContentChange(topic.id, content)}
          autoFocus={false}
          containerRef={containerRef}
          editorStyle={styles.contentEditor}
        />
      </div>

      <div style={styles.fieldWrapper}>
        <div style={styles.fieldLabel}>Image (optional)</div>
        {translationMode ? (
          topic.imageUrl ? (
            <img src={topic.imageUrl} alt={topic.imageAlt || ''} style={styles.imagePreviewImage} />
          ) : null
        ) : topic.imageUrl ? (
          <div style={styles.imagePreviewContainer}>
            <div style={styles.imagePreviewFrame}>
              <img
                src={topic.imageUrl}
                alt={topic.imageAlt || ''}
                style={styles.imagePreviewImage}
              />
            </div>
            <div style={styles.imageActions}>
              <button
                type="button"
                style={styles.imageActionButton}
                onClick={() => onImageInsert(topic.id)}
              >
                Replace
              </button>
              <button
                type="button"
                style={{ ...styles.imageActionButton, color: '#b91c1c' }}
                onClick={() => onImageRemove(topic.id)}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            style={styles.imagePlaceholder}
            onClick={() => onImageInsert(topic.id)}
            onMouseEnter={(event) => {
              event.currentTarget.style.borderColor = tokens.brand.primary;
              event.currentTarget.style.color = tokens.brand.primary;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.borderColor = tokens.borders.default;
              event.currentTarget.style.color = tokens.text.muted;
            }}
          >
            + Add Image
          </button>
        )}
      </div>
    </div>
  );
}
